import { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  Image,
} from "@chakra-ui/react";
import { getAnnouncementList } from "../../../api/getApi";
import { languageMapping } from "../../../i18n/config";
import bg from "../image/announcement_bg.png";
import closeButton from "../image/close_button.png";
import titleBg from "../image/title.png";
import titleBgActive from "../image/title_active.png";
import contentBg from "../image/content_bg.png";
import i18next from "i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { COMMON_WEB_PATH } from "../../../constant";
import AnnouncementContent from "./AnnouncementContent";

// 取得星期幾文字
const getWeekday = (dayIndex) => {
  const weekdays = [
    "common.每周日",
    "common.每周一",
    "common.每周二",
    "common.每周三",
    "common.每周四",
    "common.每周五",
    "common.每周六",
  ];
  return weekdays[dayIndex] || "";
};

const MaintenanceCard = ({ item }) => {
  const { t } = useTranslation();

  return (
    <Box
      bgImage={`url(${contentBg})`}
      bgSize="100% 100%"
      mb={4}
      p={4}
      h={"100px"}
      display={"flex"}
      alignItems="center"
      justifyContent="start"
      gap={5}
    >
      <Box
        display={"flex"}
        alignItems={"center"}
        w="60px"
        h="60px"
        mt={1}
        mb={1}
      >
        <Image
          src={`${COMMON_WEB_PATH}/ds88platformIcon/${item.platformEnum}.png`}
        />
      </Box>
      <Box>
        <Text
          color="white"
          fontWeight="bold"
          fontSize="xl"
          textShadow="0 0 5px #805AD5, 0 0 10px #6B46C1"
          mb={2}
        >
          {item.platform}
        </Text>
        <Text>{`${t(getWeekday(item.maintenance_day_of_week))} ${t("common.例行維護")}`}</Text>
        <Text color="white" fontSize="md">
          {item.maintenanceStartTime} ~ {item.maintenanceEndTime}
        </Text>
      </Box>
    </Box>
  );
};

/**
 * @description 公告彈窗元件
 * @returns {JSX.Element}
 * 自動彈出時間點:
 * 當有公告資料時、自訂 URL 參數 announcement_open=true )
 * 維護資料有無都不會自動彈出
 *
 * 彩彈出內容排序方式:(預設分頁優先級: 公告 > 維護通知)
 * 沒有公告資料，分頁優先顯示維護通知
 * 有公告資料，分頁優先顯示公告內容
 */
const AnnouncementModal = () => {
  const { t } = useTranslation();
  const [announcementList, setAnnouncementList] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();
  const openAnnouncement = searchParams.get("announcement_open");

  const isHandleAllGame = useSelector((state) => state.isHandleAllGame);

  const { allGamePlatform = [] } = isHandleAllGame;

  /**
   * 篩選出有維護時間的遊戲平台
   * 條件
   * 1. maintenanceStartTime 有值且不等於 "00:00:00"
   * 2. maintenanceEndTime 有值且不等於 "00:00:00"
   * 3. maintenance_day_of_week 有值
   */
  const maintenanceList = allGamePlatform.filter((item) => {
    const isOpenStart =
      item.maintenanceStartTime && item.maintenanceStartTime !== "00:00:00";
    const isOpenEnd =
      item.maintenanceEndTime && item.maintenanceEndTime !== "00:00:00";
    const weekDay = item.maintenance_day_of_week;
    return isOpenStart || isOpenEnd || weekDay;
  });

  // 關閉彈窗
  const onClose = () => setModalOpen(false);

  useEffect(() => {
    getAnnouncementList()
      .then((data) => {
        if (data?.data) {
          const processedData = data.data.map((item) => ({
            ...item,
            currentLng:
              item.news_language.find(
                (lngItem) =>
                  lngItem.language === languageMapping(i18next.language),
              ) || item.news_language?.[0],
          }));
          setAnnouncementList(processedData);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch announcements", err);
      });
  }, []);

  // 當有公告時自動打開彈窗
  useEffect(() => {
    if (announcementList.length > 0) {
      setModalOpen(true);
      setActiveTab(0); // 有公告，顯示公告分頁
    } else {
      setActiveTab(1); // 沒有公告，顯示維護通知分頁
    }
  }, [announcementList]);

  // 透過 URL 參數打開公告彈窗
  useEffect(() => {
    if (openAnnouncement === "true") {
      setModalOpen(true);

      const currentParams = new URLSearchParams(location.search);
      currentParams.delete("announcement_open");

      setSearchParams(currentParams);
    }
  }, [openAnnouncement, location, setSearchParams]);

  return (
    <Modal isOpen={modalOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent
        height={{ base: "450px", sm: "600px" }}
        width={"450px"}
        pb={12}
        marginX={"1em"}
        sx={{
          bgImage: `url(${bg})`,
          bgSize: "100% 100%",
          background: "none",
        }}
      >
        <ModalHeader
          pb={0}
          pt={6}
          minH="85px"
          display={"flex"}
          alignItems={"center"}
        >
          <Box
            h="100%"
            w="150px"
            bgSize={"100% 100%"}
            bgImage={`url(${activeTab === 0 ? titleBgActive : titleBg})`}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            bgPos="center"
            fontSize="1em"
            cursor={"pointer"}
            _hover={{
              bgImage: `url(${titleBgActive})`,
              bgSize: "100% 110%",
            }}
            onClick={() => setActiveTab(0)}
          >
            <Text mr={4}>{t("common.公告")}</Text>
          </Box>
          <Box
            h="100%"
            w="150px"
            bgSize={"100% 110%"}
            bgImage={`url(${activeTab === 1 ? titleBgActive : titleBg})`}
            bgPos="center"
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            fontSize="1em"
            ml={-5}
            cursor={"pointer"}
            onClick={() => setActiveTab(1)}
            _hover={{
              bgImage: `url(${titleBgActive})`,
              bgSize: "100% 110%",
            }}
          >
            <Text mr={4}>{t("common.維護通知_title")}</Text>
          </Box>
        </ModalHeader>
        <ModalCloseButton
          boxSize={10}
          sx={{
            position: "absolute",
            top: -12,
            right: 0,
          }}
        >
          <Image src={closeButton} alt="Close" />
        </ModalCloseButton>
        <ModalBody overflow={"scroll"}>
          {activeTab === 0 ? (
            <AnnouncementContent announcementList={announcementList} />
          ) : (
            <Box>
              <Text color="white" mb={4} fontSize="sm">
                {t("common.maintenance_customer_service_hint")}
              </Text>
              {maintenanceList && maintenanceList.length > 0 ? (
                <>
                  <Text color="white" mb={2} fontSize="sm" fontWeight="bold">
                    {t("common.maintenance_weekly_hint")}
                  </Text>
                  {maintenanceList.map((item) => (
                    <MaintenanceCard item={item} key={item.platformUid} />
                  ))}
                </>
              ) : (
                <Text color="white" textAlign="center" mt={10}>
                  {t("common.目前沒有維護通知")}
                </Text>
              )}
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AnnouncementModal;
